/**
 * PhotoSwipeGallery - Custom element for creating responsive media galleries
 * Uses PhotoSwipe library to show images and videos in a lightbox modal
 */
class PhotoSwipeGallery extends HTMLElement {
  constructor() {
    super();
    this.videoElements = new Map();
    this.fullscreenAPI = this.getFullscreenAPI();
    this.boundListeners = new Map();
    this.lightbox = null;
    this.iconCache = new Map();
  }

  /**
   * Called when the element is inserted into the DOM
   * Web Components lifecycle method
   */
  connectedCallback() {
    this.prefetchIcons(['chevron-left', 'chevron-right', 'close', 'expand']);

    this.initGallery();
  }

  /**
   * Called when the element is removed from the DOM
   * Cleanup event listeners and resources
   */
  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * Clean up resources and event listeners
   */
  cleanup() {
    // Close lightbox if it's open
    if (this.lightbox && this.lightbox.pswp && this.lightbox.pswp.isOpen) {
      this.lightbox.pswp.close();
    }

    // Destroy the lightbox
    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }

    // Clean up stored video elements
    this.videoElements.forEach((video) => {
      video.pause();
      video.src = '';
      video.load();
    });
    this.videoElements.clear();

    // Remove stored event listeners
    this.boundListeners.forEach((handler, element) => {
      element.removeEventListener('click', handler);
    });
    this.boundListeners.clear();

    // Remove fullscreen listeners if any
    if (this.fullscreenAPI) {
      document.removeEventListener(this.fullscreenAPI.change, this.fullscreenChangeHandler);
      document.removeEventListener(this.fullscreenAPI.error, this.fullscreenErrorHandler);
    }
  }

  /**
   * Prefetch important icons
   * @param {Array} iconNames - Names of icons to prefetch
   */
  prefetchIcons(iconNames) {
    iconNames.forEach((iconName) => {
      this.loadSvgIcon(iconName).catch((error) => {
        console.warn(`Error prefetching icon ${iconName}:`, error);
      });
    });
  }

  /**
   * Load an SVG icon from Shopify CDN
   * @param {string} iconName - Name of the icon to load
   * @returns {Promise<string>} Promise resolving to SVG content
   */
  async loadSvgIcon(iconName) {
    const rootStyles = getComputedStyle(document.documentElement);
    const brand = window.generalProperties.styleguide_brand;
    const iconUrl = document
      .querySelector('#PhotoSwipeStyle')
      .href.replace(/(photoswipe.css)/g, `icon-${brand}-${iconName}-24.svg`);
    try {
      const response = await fetch(iconUrl);
      if (!response.ok) {
        throw new Error(`Failed to load icon: ${response.status}`);
      }

      const svgText = await response.text();

      // Extract SVG content and add classes
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
      const svgElement = svgDoc.querySelector('svg');

      if (svgElement) {
        svgElement.classList.add('pswp__icon');
        svgElement.setAttribute('aria-hidden', 'false');
        svgElement.setAttribute('width', '24');
        svgElement.setAttribute('height', '24');

        // Add specific class based on icon name
        svgElement.classList.add(`icon-${iconName}`);

        const serializer = new XMLSerializer();
        const processedSvg = serializer.serializeToString(svgElement);

        // Cache the processed SVG
        this.iconCache.set(iconName, processedSvg);
        return processedSvg;
      }

      throw new Error('Invalid SVG structure');
    } catch (error) {
      console.error(`Error loading icon ${iconName}:`, error);

      // Return empty string on error and don't cache (will try again next time)
      return '';
    }
  }

  /**
   * Get icon SVG with fallback
   * @param {string} iconName - Name of the icon
   * @param {Object} options - Additional options like aria-label
   * @returns {string} SVG HTML string
   */
  async getIcon(iconName, options = {}) {
    // Try to load from CDN
    const svgContent = await this.loadSvgIcon(iconName);

    if (svgContent) {
      // If we have SVG content, patch in any additional attributes
      if (options.ariaLabel) {
        return svgContent.replace('aria-hidden="false"', `aria-hidden="false" aria-label="${options.ariaLabel}"`);
      }
      return svgContent;
    }

    // Fallback icons if CDN failed
    const fallbackIcons = {
      close: `<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-${iconName} icon-24 icon-${brand}-${iconName}-24" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M21.3535 3.35352L20.6465 2.64648L12 11.293L3.35352 2.64648L2.64648 3.35352L11.293 12L2.64648 20.6465L3.35352 21.3535L12 12.707L20.6465 21.3535L21.3535 20.6465L12.707 12L21.3535 3.35352Z" fill="currentColor"/></svg>`,
      'chevron-left': `<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-${iconName} icon-24 icon-${brand}-${iconName}-24" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15.25 19.457L8.14648 12.3535C7.95117 12.1582 7.95117 11.8418 8.14648 11.6465L15.25 4.54297L15.957 5.25L9.20703 12L15.957 18.75L15.25 19.457Z" fill="currentColor"/></svg>`,
      'chevron-right': `<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-${iconName} icon-24 icon-${brand}-${iconName}-24" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M8.75 19.457L8.04297 18.75L14.793 12L8.04297 5.25L8.75 4.54297L15.8535 11.6465C16.0488 11.8418 16.0488 12.1582 15.8535 12.3535L8.75 19.457Z" fill="currentColor"/></svg>`,
      expand: `<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-${iconName} icon-24 icon-${brand}-${iconName}-24" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M20.5 3H15v1h5v5h1V3.5a.5.5 0 0 0-.5-.5ZM20 20h-5v1h5.5a.5.5 0 0 0 .5-.5V15h-1v5ZM3 3.5V9h1V4h5V3H3.5a.5.5 0 0 0-.5.5ZM4 15H3v5.5a.5.5 0 0 0 .5.5H9v-1H4v-5Z" fill="currentColor"/></svg>`,
    };

    return fallbackIcons[iconName] || '';
  }

  /**
   * Initialize the gallery by gathering media items and setting up the lightbox
   */
  async initGallery() {
    const media = this.querySelectorAll('button[data-pswp-src]');
    const mediaArray = Array.from(media);

    const items = this.createItemsFromMedia(mediaArray);
    await this.setupLightbox(items, mediaArray);
  }

  /**
   * Create gallery items from media DOM elements
   * @param {Array} mediaArray - Array of media DOM elements
   * @returns {Array} Array of gallery items with proper properties
   */
  createItemsFromMedia(mediaArray) {
    const items = mediaArray.map((asset) => ({
      id: asset.dataset.index,
      width: asset.dataset.pswpWidth,
      height: asset.dataset.pswpHeight,
      type: asset.dataset.type,
      src: asset.dataset.pswpSrc,
      loop: JSON.parse(asset.dataset.pswpLoop || false),
    }));

    // Ensure no duplicated ids
    return [...new Map(items.map((item) => [item.id, item])).values()];
  }

  /**
   * Set up the PhotoSwipe lightbox with all necessary event handlers
   * @param {Array} items - Array of gallery items
   * @param {NodeList} triggers - Collection of DOM elements that trigger the lightbox
   */
  async setupLightbox(items, triggers) {
    const closeIcon = await this.getIcon('close');
    const fullScreenIcon = await this.getIcon('expand', {
      ariaLabel: 'Toggle fullscreen',
    });

    const chevronLeftIcon = await this.getIcon('chevron-left', {
      ariaLabel: 'Previous',
    });

    const chevronRightIcon = await this.getIcon('chevron-right', {
      ariaLabel: 'Next',
    });

    this.lightbox = new PhotoSwipeLightbox({
      dataSource: items,
      pswpModule: PhotoSwipe,
      imageClickAction: 'zoom',
      tapAction: 'zoom',
      preloadFirstSlide: true,
      bgOpacity: 1,
      counter: false,
      zoom: false,
      arrowPrev: false,
      arrowNext: false,
      preload: [1, 4],
      loop: false,
      preloaderDelay: 0,
      closeSVG: closeIcon,
    });

    this.attachTriggerEventListeners(triggers, this.lightbox);
    this.registerLightboxEventHandlers(this.lightbox);

    this.lightbox.on('uiRegister', (e) => {
      if (this.fullscreenAPI) {
        this.lightbox.pswp.ui.registerElement({
          name: 'fullscreen',
          ariaLabel: 'Toggle fullscreen',
          order: 9,
          isButton: true,
          html: fullScreenIcon,
          onClick: async () => {
            this.toggleFullscreen();
          },
        });
      }

      this.lightbox.pswp.ui.registerElement({
        name: 'arrow-left',
        order: 0,
        className: 'pswp__button--arrows pswp__button--arrow--left',
        isButton: true,
        html: chevronLeftIcon,
        onInit: (el, pswp) => {
          this.setupArrowState(el, pswp, true);
        },
        onClick: () => {
          pswp.prev();
        },
      });

      // Right arrow
      this.lightbox.pswp.ui.registerElement({
        name: 'arrow-right',
        order: 1,
        className: 'pswp__button--arrows pswp__button--arrow--right',
        isButton: true,
        html: chevronRightIcon,
        onInit: (el, pswp) => {
          this.setupArrowState(el, pswp, false);
        },
        onClick: () => {
          pswp.next();
        },
      });

      this.lightbox.pswp.ui.registerElement({
        name: 'dots',
        className: 'pswp__dots',
        order: 2,
        onInit: (el, pswp) => {
          this.createNavigationDots(el, pswp);
        },
      });

      // Register play/pause button for videos
      this.registerPlayPauseButton(this.lightbox.pswp);
    });

    this.lightbox.init();
  }

  /**
   * Attach click event listeners to gallery trigger elements
   * @param {NodeList} triggers - Collection of DOM elements that trigger the lightbox
   * @param {Object} lightbox - PhotoSwipe lightbox instance
   */
  attachTriggerEventListeners(triggers, lightbox) {
    triggers.forEach((mediaItem, index) => {
      // Skip video items (they don't open the lightbox directly)
      if (mediaItem.getAttribute('data-type') === 'video') {
        return;
      }

      // Create a bound listener function that we can remove later
      const clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        lightbox.loadAndOpen(index);
      };

      // Store the handler for cleanup
      this.boundListeners.set(mediaItem, clickHandler);

      // Add the event listener
      mediaItem.addEventListener('click', clickHandler);
    });
  }

  /**
   * Register all event handlers for the PhotoSwipe lightbox
   * @param {Object} lightbox - PhotoSwipe lightbox instance
   */
  registerLightboxEventHandlers(lightbox) {
    this.registerContentHandlers(lightbox);
  }

  /**
   * Register content-related event handlers (load, append, remove, activate)
   * @param {Object} lightbox - PhotoSwipe lightbox instance
   */
  registerContentHandlers(lightbox) {
    // Content loading handler
    lightbox.on('contentLoad', (e) => {
      const { content } = e;
      if (content.data.type === 'video') {
        this.handleVideoContentLoad(content);
      }
    });

    // Content append handler
    lightbox.on('contentAppend', (e) => {
      const { content } = e;
      if (content.data.type === 'video' && content.videoElement) {
        e.preventDefault(); // Prevent default to handle video appending ourselves
        this.handleVideoContentAppend(content);
      }
    });

    // Content removal handler
    lightbox.on('contentRemove', (e) => {
      const { content } = e;
      if (content.data.type === 'video' && content.videoElement && content.videoElement.parentNode) {
        content.videoElement.pause();
        content.videoElement.parentNode.removeChild(content.videoElement);
      }
    });

    // Content activation handler
    lightbox.on('contentActivate', (e) => {
      const { content } = e;
      if (content.data.type === 'video' && content.videoElement) {
        const playPromise = content.videoElement.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.log('Auto-play prevented:', error);
          });
        }
      }
    });

    // Slide change handler
    lightbox.on('change', () => {
      window.pauseAllMedia();
      const currSlide = lightbox.pswp.currSlide;
      if (currSlide.data.type === 'video') {
        const videoElement = currSlide.container.querySelector('.pswp__video');
        if (videoElement) {
          videoElement.play();
        }
      }
    });

    // Destroy handler
    lightbox.on('destroy', () => {
      this.videoElements.clear();
    });
  }

  /**
   * Handle video content loading
   * @param {Object} content - Content object from PhotoSwipe
   */
  handleVideoContentLoad(content) {
    let videoElement = this.videoElements.get(content.data.id);

    if (!videoElement) {
      videoElement = this.createVideoElement(content.data);
      this.videoElements.set(content.data.id, videoElement);
    }

    content.videoElement = videoElement;
    content.state = 'loading';

    if (videoElement.readyState >= 3) {
      content.onLoaded();
    } else {
      videoElement.onloadeddata = () => content.onLoaded();
      videoElement.onerror = () => content.onError();
    }
  }

  /**
   * Create a video element for the lightbox
   * @param {Object} data - Video data from PhotoSwipe
   * @returns {HTMLElement} The created video element
   */
  createVideoElement(data) {
    const videoElement = document.createElement('video');
    videoElement.className = 'pswp__content pswp__video';
    videoElement.setAttribute('control', false);
    videoElement.setAttribute('autoplay', '');
    videoElement.setAttribute('playsinline', '');

    if (data.loop) {
      videoElement.setAttribute('loop', '');
    }

    const sourceVideo = document.createElement('source');
    sourceVideo.src = data.src;
    sourceVideo.type = 'video/mp4';
    videoElement.appendChild(sourceVideo);

    return videoElement;
  }

  /**
   * Handle video content appending to the DOM
   * @param {Object} content - Content object from PhotoSwipe
   */
  handleVideoContentAppend(content) {
    // Only append if not already in DOM
    if (!content.videoElement.parentNode) {
      // Add play/pause handler if not already added
      if (!content.videoElement.hasPlayPauseHandler) {
        this.setupVideoControls(content);
      }
      content.slide.container.appendChild(content.videoElement);
    }
  }

  /**
   * Set up video controls and event handlers
   * @param {Object} content - Content object from PhotoSwipe
   */
  setupVideoControls(content) {
    content.videoElement.hasPlayPauseHandler = true;

    // Click handler to toggle play/pause
    const videoClickHandler = (e) => {
      e.stopPropagation();
      e.preventDefault();

      const playButton = content.slide.container.querySelector('.play');
      const pauseButton = content.slide.container.querySelector('.pause');

      if (content.videoElement.paused) {
        content.videoElement.play();
        if (playButton && pauseButton) {
          playButton.hidden = true;
          pauseButton.hidden = false;
        }
      } else {
        content.videoElement.pause();
        if (playButton && pauseButton) {
          playButton.hidden = false;
          pauseButton.hidden = true;
        }
      }
      return false;
    };

    // Store the handler for cleanup
    this.boundListeners.set(content.videoElement, videoClickHandler);

    // Add the event listener
    content.videoElement.addEventListener('click', videoClickHandler);

    // Video state change handlers
    this.setupVideoStateHandlers(content);
  }

  /**
   * Setup video state change handlers (play, pause, ended)
   * @param {Object} content - Content object from PhotoSwipe
   */
  setupVideoStateHandlers(content) {
    // Create handlers for video state changes
    const playHandler = () => {
      const pswpElement = content.slide ? content.slide.pswp.element : content.instance.element;
      const playButton = pswpElement.querySelector('.play');
      const pauseButton = pswpElement.querySelector('.pause');
      if (playButton && pauseButton) {
        playButton.hidden = true;
        pauseButton.hidden = false;
      }
    };

    const pauseHandler = () => {
      const pswpElement = content.slide ? content.slide.pswp.element : content.instance.element;
      const playButton = pswpElement.querySelector('.play');
      const pauseButton = pswpElement.querySelector('.pause');
      if (playButton && pauseButton) {
        playButton.hidden = false;
        pauseButton.hidden = true;
      }
    };

    const endedHandler = () => {
      const pswpElement = content.slide ? content.slide.pswp.element : content.instance.element;

      const playButton = pswpElement.querySelector('.play');
      const pauseButton = pswpElement.querySelector('.pause');
      if (playButton && pauseButton) {
        playButton.hidden = false;
        pauseButton.hidden = true;
      }
    };

    // Store handlers with unique keys for cleanup
    // We use a combination of the element and event name as the key
    this.boundListeners.set(content.videoElement.toString() + '_play', playHandler);
    this.boundListeners.set(content.videoElement.toString() + '_pause', pauseHandler);
    this.boundListeners.set(content.videoElement.toString() + '_ended', endedHandler);

    // Add event listeners
    content.videoElement.addEventListener('play', playHandler);
    content.videoElement.addEventListener('pause', pauseHandler);
    content.videoElement.addEventListener('ended', endedHandler);
  }

  /**
   * Set up arrow state (enabled/disabled) based on current slide
   * @param {HTMLElement} el - Arrow element
   * @param {Object} pswp - PhotoSwipe instance
   * @param {boolean} isLeftArrow - Whether this is the left arrow
   */
  setupArrowState(el, pswp, isLeftArrow) {
    const updateArrowState = () => {
      const isFirstSlide = pswp.currIndex === 0;
      const isLastSlide = pswp.currIndex === pswp.options.dataSource.length - 1;

      if ((isLeftArrow && isFirstSlide) || (!isLeftArrow && isLastSlide)) {
        el.setAttribute('disabled', 'disabled');
      } else {
        el.removeAttribute('disabled');
      }
    };

    // Set initial state
    updateArrowState();

    // Update on slide change
    pswp.on('change', updateArrowState);
  }

  /**
   * Create navigation dots for each slide
   * @param {HTMLElement} el - Dots container element
   * @param {Object} pswp - PhotoSwipe instance
   */
  createNavigationDots(el, pswp) {
    const totalSlides = pswp.options.dataSource.length;
    const dots = [];

    // Create dots
    for (let i = 0; i < totalSlides; i++) {
      const dot = document.createElement('button');
      dot.className = 'pswp__dot' + (i === pswp.currIndex ? ' pswp__dot--active' : '');
      dot.dataset.index = i;

      // Create and store click handler
      const dotClickHandler = (e) => {
        pswp.goTo(parseInt(e.target.dataset.index));
      };

      // Store the handler for cleanup
      this.boundListeners.set(dot, dotClickHandler);

      // Add event listener
      dot.addEventListener('click', dotClickHandler);
      el.appendChild(dot);
      dots.push(dot);
    }

    // Create and store change handler
    const dotsChangeHandler = () => {
      dots.forEach((dot, idx) => {
        if (idx === pswp.currIndex) {
          dot.classList.add('pswp__dot--active');
        } else {
          dot.classList.remove('pswp__dot--active');
        }
      });
    };

    // Store the handler with a unique key for cleanup
    this.boundListeners.set('dots_change_handler', dotsChangeHandler);

    // Update active dot on slide change
    pswp.on('change', dotsChangeHandler);
  }

  /**
   * Register play/pause button for videos
   * @param {Object} pswp - PhotoSwipe instance
   */
  registerPlayPauseButton(pswp) {
    pswp.ui.registerElement({
      name: 'play-pause-button',
      isButton: false,
      html: '<div class="video__controls"><button class="video__control play"><svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg"><mask id="play" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24"><path d="m6 20 14-8L6 4v16Z" fill="currentColor"></path></mask><g mask="url(#play)"><path fill="currentColor" d="M0 0h24v24H0z"></path></g></svg></button><button class="video__control pause" hidden=""><svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg"><mask id="pause" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24"><path d="M10 4H6v16h4V4ZM18 4h-4v16h4V4Z" fill="currentColor"></path></mask><g mask="url(#pause)"><path fill="currentColor" d="M0 0h24v24H0z"></path></g></svg></button></div>',
      appendTo: 'wrapper',
      onClick: (event, el) => {
        this.toggleVideoPlayback(pswp, el);
      },
      onInit: (el, pswp) => {
        this.setupPlayPauseButton(el, pswp);
      },
    });
  }

  /**
   * Toggle video playback
   * @param {Object} pswp - PhotoSwipe instance
   * @param {HTMLElement} el - Button element
   */
  toggleVideoPlayback(pswp, el) {
    const videoElement = pswp.currSlide.container.querySelector('.pswp__video');
    if (!videoElement) return;

    const playButton = el.querySelector('.play');
    const pauseButton = el.querySelector('.pause');

    if (videoElement.paused) {
      videoElement.play();
      playButton.hidden = true;
      pauseButton.hidden = false;
    } else {
      videoElement.pause();
      playButton.hidden = false;
      pauseButton.hidden = true;
    }
  }

  /**
   * Set up play/pause button
   * @param {HTMLElement} el - Button element
   * @param {Object} pswp - PhotoSwipe instance
   */
  setupPlayPauseButton(el, pswp) {
    const playButton = el.querySelector('.play');
    const pauseButton = el.querySelector('.pause');

    const isVideoSlide = () => {
      const currentSlide = pswp.options.dataSource[pswp.currIndex];
      return (currentSlide && currentSlide.type === 'video') || false;
    };

    pswp.on('change', () => {
      if (isVideoSlide()) {
        el.style.display = 'block';
        const videoElement = pswp.currSlide.container.querySelector('.pswp__video');

        if (videoElement) {
          this.setupVideoWrapper(videoElement, playButton, pauseButton);

          // Reset button states based on current video state
          this.updatePlayPauseState(videoElement, playButton, pauseButton);
        }
      } else {
        el.style.display = 'none';
      }
    });
  }

  /**
   * Set up video wrapper with click event
   * @param {HTMLElement} videoElement - Video element
   * @param {HTMLElement} playButton - Play button
   * @param {HTMLElement} pauseButton - Pause button
   */
  setupVideoWrapper(videoElement, playButton, pauseButton) {
    const videoElementWrapper = document.querySelector('.pswp__zoom-wrap');

    // Create and store bound handlers for later removal
    const wrapperClickHandler = (e) => {
      e.stopPropagation();
      e.preventDefault();

      if (videoElement.paused) {
        videoElement.play();
        playButton.hidden = true;
        pauseButton.hidden = false;
      } else {
        videoElement.pause();
        playButton.hidden = false;
        pauseButton.hidden = true;
      }
    };

    const videoEndedHandler = () => {
      playButton.hidden = false;
      pauseButton.hidden = true;
    };

    // Store the handlers for cleanup
    this.boundListeners.set(videoElementWrapper, wrapperClickHandler);
    this.boundListeners.set(videoElement, videoEndedHandler);

    // Add the event listeners
    videoElementWrapper.addEventListener('click', wrapperClickHandler);
    videoElement.addEventListener('ended', videoEndedHandler);
  }

  /**
   * Update play/pause button state based on video state
   * @param {HTMLElement} videoElement - Video element
   * @param {HTMLElement} playButton - Play button
   * @param {HTMLElement} pauseButton - Pause button
   */
  updatePlayPauseState(videoElement, playButton, pauseButton) {
    if (videoElement.paused || videoElement.ended) {
      playButton.hidden = false;
      pauseButton.hidden = true;
    } else {
      playButton.hidden = true;
      pauseButton.hidden = false;
    }
  }

  /**
   * Toggle fullscreen mode
   */
  toggleFullscreen() {
    if (!this.fullscreenAPI) return;

    if (this.fullscreenAPI.isFullscreen()) {
      // Exit full-screen mode
      this.fullscreenAPI.exit();
      // Toggle icon display
      setTimeout(() => {
        document.getElementById('pswp__icn-fullscreen-exit').style.display = 'none';
        document.getElementById('pswp__icn-fullscreen-request').style.display = 'inline';
      }, 300);
    } else {
      // Enter full-screen mode
      this.fullscreenAPI.request(document.querySelector('.pswp'));
      // Toggle icon display
      setTimeout(() => {
        document.getElementById('pswp__icn-fullscreen-exit').style.display = 'inline';
        document.getElementById('pswp__icn-fullscreen-request').style.display = 'none';
      }, 300);
    }
  }

  /**
   * Get the appropriate fullscreen API for the browser
   * @returns {Object|null} Fullscreen API object or null if not supported
   */
  getFullscreenAPI() {
    let api = null;
    let enterFS;
    let exitFS;
    let elementFS;
    let changeEvent;
    let errorEvent;

    if (document.fullscreenEnabled) {
      enterFS = 'requestFullscreen';
      exitFS = 'exitFullscreen';
      elementFS = 'fullscreenElement';
      changeEvent = 'fullscreenchange';
      errorEvent = 'fullscreenerror';
    } else if (document.webkitFullscreenEnabled) {
      enterFS = 'webkitRequestFullscreen';
      exitFS = 'webkitExitFullscreen';
      elementFS = 'webkitFullscreenElement';
      changeEvent = 'webkitfullscreenchange';
      errorEvent = 'webkitfullscreenerror';
    }

    if (enterFS) {
      api = {
        request: function (el) {
          if (enterFS === 'webkitRequestFullscreen') {
            el[enterFS](Element.ALLOW_KEYBOARD_INPUT);
          } else {
            el[enterFS]();
          }
        },
        exit: function () {
          return document[exitFS]();
        },
        isFullscreen: function () {
          return document[elementFS];
        },
        change: changeEvent,
        error: errorEvent,
      };
    }

    return api;
  }

  /**
   * Get a container for fullscreen mode
   * @returns {HTMLElement} Container element
   */
  getContainer() {
    const pswpContainer = document.createElement('div');
    pswpContainer.style.background = 'var(--background-surface)';
    pswpContainer.style.width = '100%';
    pswpContainer.style.height = '100%';
    document.body.appendChild(pswpContainer);
    return pswpContainer;
  }

  /**
   * Get a promise that resolves when fullscreen is ready
   * @returns {Promise} Promise that resolves when fullscreen is ready
   */
  getFullscreenPromise() {
    const pswpContainer = this.getContainer();
    const fullscreenAPI = this.fullscreenAPI;

    return new Promise((resolve) => {
      if (!fullscreenAPI) {
        resolve();
        return;
      }

      if (fullscreenAPI.isFullscreen()) {
        resolve();
        return;
      }

      // Create and store handlers for later removal if needed
      this.fullscreenChangeHandler = () => {
        setTimeout(() => {
          resolve();
        }, 300);
      };

      this.fullscreenErrorHandler = () => {
        console.error('Fullscreen error');
        resolve(); // Still resolve to continue
      };

      // Add listener for fullscreen change
      document.addEventListener(fullscreenAPI.change, this.fullscreenChangeHandler, { once: true });

      // Add error handler
      document.addEventListener(fullscreenAPI.error, this.fullscreenErrorHandler, { once: true });

      // Request fullscreen
      try {
        fullscreenAPI.request(pswpContainer);
      } catch (e) {
        console.error('Fullscreen request failed:', e);
        resolve(); // Still resolve to continue
      }
    });
  }
}

// Register the custom element
customElements.define('photoswipe-media-gallery', PhotoSwipeGallery);
